import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useMemo } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ScaledPreview } from "@/components/scaled-preview/scaled-preview";
import { convertPercentToEmPosition } from "@/components/scaled-preview/em-positioning";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { useCustomTemplates, useDeleteTemplate } from "@/api/template.api";
import { TemplateDto, TemplateElement } from "@exibra/shared";
import { cn } from "@/lib/utils";

// Update styles for centered preview and ensure controls are always on top
const styles = {
    previewContent: "absolute inset-0 bg-background",
    cardOverlay: "absolute inset-0 flex flex-col z-9999",
    cardHeader: "bg-gradient-to-b from-background/90 to-background/0 p-3",
    cardActions:
        "mt-auto bg-gradient-to-t from-background/90 to-background/0 p-3 flex justify-end gap-2",
};

export const Templates = () => {
    const selectedTenant = useTenantStore((state) => state.selectedTenant);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { data: customTemplates } = useCustomTemplates(selectedTenant?.id);
    const { mutate: deleteTemplate } = useDeleteTemplate();

    const templates = useMemo(() => {
        return (customTemplates ?? []).map((template) => {
            return {
                ...template,
                elements: template.elements.map((element) => {
                    const converted = convertPercentToEmPosition(element);
                    // If element is a text with placeholder, ensure it has highest z-index
                    if (converted.type === "text" && converted.content) {
                        if (
                            converted.content.includes("{APPRAISER_NAME}") ||
                            converted.content.includes("{TUNNEL_NUMBER}")
                        ) {
                            converted.style = {
                                ...converted.style,
                                zIndex: 9999, // Ensure placeholders are always on top
                            };
                        }
                    }
                    return converted;
                }),
            };
        });
    }, [customTemplates]);

    const handleCreateTemplate = () => {
        navigate("new");
    };

    const handleEditTemplate = (template: TemplateDto) => {
        console.log("template", template);
        navigate(`edit/${template.id}`);
    };

    // Reusable function to render text elements using the wrapper pattern
    const renderTextElement = (element: TemplateElement, index: number, template: TemplateDto) => {
        const { style = {} } = element;

        // Extract positioning properties - these represent the true center point
        const { left = "8em", top = "4.5em", fontSize = "1.6em", ...otherStyles } = style;

        return (
            <div
                key={element.id || index}
                className="absolute"
                style={{
                    left,
                    top,
                    position: "absolute",
                }}
            >
                <div
                    style={{
                        fontSize,
                        fontWeight: style.fontWeight,
                        fontStyle: style.fontStyle,
                        textDecoration: style.textDecoration,
                        color: style.color || "#000000",
                        textAlign: style.textAlign || "center",
                        whiteSpace: "nowrap",
                        userSelect: "none",
                        ...otherStyles,
                    }}
                >
                    {element.content}
                </div>
            </div>
        );
    };

    return (
        <>
            <title>{t("templates.title")}</title>
            <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                    <h1>{t("templates.title")}</h1>
                    <Button onClick={handleCreateTemplate}>{t("general.add")}</Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map((template) => (
                        <Card key={template.id} className="relative">
                            <ScaledPreview>
                                <div
                                    className={styles.previewContent}
                                    style={{ backgroundColor: template.style?.backgroundColor }}
                                >
                                    {/* Sort elements by z-index before rendering */}
                                    {[...template.elements]
                                        .sort(
                                            (a, b) =>
                                                (Number(a.style?.zIndex) || 0) -
                                                (Number(b.style?.zIndex) || 0),
                                        )
                                        .map((element, index) => {
                                            if (element.type === "image") {
                                                return element.content ? (
                                                    <img
                                                        key={element.id || index}
                                                        src={
                                                            import.meta.env.VITE_API_URL +
                                                            element.content
                                                        }
                                                        alt="Template background"
                                                        className="absolute object-cover"
                                                        style={element.style}
                                                    />
                                                ) : null;
                                            }

                                            if (element.type === "container") {
                                                return (
                                                    <div
                                                        key={element.id || index}
                                                        className="absolute"
                                                        style={element.style}
                                                    />
                                                );
                                            }

                                            if (element.type === "text") {
                                                return renderTextElement(element, index, template);
                                            }

                                            return null;
                                        })}
                                </div>
                            </ScaledPreview>

                            {/* Overlay Layer with Header and Actions - now with explicit z-index */}
                            <div className={cn(styles.cardOverlay)} style={{ zIndex: 9999 }}>
                                <div className={styles.cardHeader}>
                                    <h3 className="text-base font-semibold">{template.name}</h3>
                                </div>
                                <div className={styles.cardActions}>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() =>
                                            deleteTemplate({
                                                templateId: template.id,
                                                tenantId: selectedTenant?.id ?? template.tenantId,
                                            })
                                        }
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                    <Button
                                        variant="default"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => handleEditTemplate(template)}
                                    >
                                        <Pencil className="h-3 w-3" />
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </>
    );
};
